<script setup lang="ts">
import { computed, nextTick, ref, toRef, watch } from 'vue'
import DOMPurify from 'dompurify'
import { NXGDSVScrollContainer, NXGDSDivider } from '@nsftx/seven-nxg-design-system'
import STStaticContentTableOfContents from './STStaticContentTableOfContents.vue'
import type StaticContentProps from './interfaces/StaticContentProps'
import useStaticContentHeadings from '@/composables/useStaticContentHeadings/useStaticContentHeadings'

const { title, content, summary } = defineProps<StaticContentProps>()
const contentRef = toRef(() => content)
const { headings, processedContent, activeHeadingId, observeHeadings } =
  useStaticContentHeadings(contentRef)

const mainSectionRef = ref<HTMLElement | null>(null)

watch(
  processedContent,
  async () => {
    await nextTick()
    if (mainSectionRef.value) observeHeadings(mainSectionRef.value)
  },
  { immediate: true, once: true }
)

const shouldShowSummary = computed(
  () => summary && summary !== '<div class=\"payload-richtext\"></div>'
)

const sanitizedSummary = computed(() => (summary ? DOMPurify.sanitize(summary) : undefined))
const sanitizedMainSection = computed(() =>
  processedContent.value ? DOMPurify.sanitize(processedContent.value) : undefined
)
</script>

<template>
  <STStaticContentTableOfContents v-model="activeHeadingId" :headings />
  <NXGDSVScrollContainer container-class="st-flex-fill">
    <div class="static-content-container">
      <div class="static-content-wrapper">
        <div class="static-content-title nxgds-type-h1-medium">{{ title }}</div>
        <NXGDSDivider />

        <div v-if="shouldShowSummary">
          <div
            class="static-content-summary static-content-text nxgds-type-paragraph-big-regular"
            v-html="sanitizedSummary"
          ></div>
          <NXGDSDivider />
        </div>

        <div
          ref="mainSectionRef"
          class="static-content-main-section static-content-text"
          v-html="sanitizedMainSection"
        ></div>
      </div>
    </div>
  </NXGDSVScrollContainer>
</template>

<style scoped lang="scss">
@use '@nsftx/seven-nxg-design-system/style/mixins' as *;

.static-content {
  &-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: var(--nxgds-alias-spacing-5xl) var(--nxgds-alias-spacing-lg);
    gap: var(--nxgds-alias-spacing-lg);
  }

  &-wrapper {
    display: flex;
    flex-direction: column;
    align-self: center;
    min-width: 400px;
    max-width: 780px;
    gap: var(--nxgds-alias-spacing-2xl);
  }

  &-title {
    color: var(--nxgds-mapped-color-text-primary);
  }

  &-main-section {
    :deep(h1) {
      color: var(--nxgds-mapped-color-text-primary);
      @include typography('h2', 'medium');
      padding-bottom: 24px;
    }
  }

  &-summary {
    padding: var(--nxgds-alias-spacing-xl);
    margin-bottom: var(--nxgds-alias-spacing-2xl);
    border-radius: var(--nxgds-alias-radius-corner-theme);
    border: 1px solid var(--nxgds-mapped-color-border-divider);

    :deep(.payload-richtext) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      gap: var(--nxgds-alias-spacing-md);
    }

    :deep(h1) {
      color: var(--nxgds-mapped-color-text-highlight);
      @include typography('h3', 'medium');
    }
  }

  &-text {
    color: var(--nxgds-mapped-color-text-secondary);

    :deep(a) {
      color: var(--nxgds-mapped-color-text-action) !important;
      text-decoration: underline;
    }

    :deep(img) {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      align-self: stretch;
      aspect-ratio: 16/9;
      border-radius: var(--nxgds-alias-radius-corner-theme);
    }
  }
}
</style>
